import { DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API } from '../../../global/config/api';
import { getFilters } from '../../../global/helpers/filters';
import { useServerCall } from '../../../global/helpers/hooks';
import CodelistStore from '../../../global/stores/CodelistStore';
import { IAttachment } from '../../../global/types/types';
import { XsBaseDialog, XsButton, XsDropzone, XsNumberField, XsSearchSelect } from '../../../global/ui';
import { ICodelistRow, IDeviceRow } from '../../../swagger/gd/codelist';
import AddNewDeviceDialog from './AddNewDeviceDialog';
import DeviceDialogStore from './DeviceDialogStore';

export interface IInstalledDeviceSelection {
  device: IDeviceRow;
  // code_ext zvoleného druhu zariadenia (napr. 'VC' = Menič napätia) — DeviceDialog podľa neho
  // rozhoduje, do ktorej tabuľky riadok patrí
  deviceTypeCode: string;
  // Zobrazovaný názov zvolenej špecifikácie — pre stĺpec "Špecifikácia zariadenia"
  specificationName: string;
  // _id zvolenej špecifikácie a code_ext zvoleného výrobcu — uložené v mock riadku (DeviceDialog),
  // aby sa dali znova predvyplniť polia Druh/Špecifikácia/Výrobca pri neskoršej editácii riadku
  specificationId: string;
  manufacturerCode: string;
  count: number;
  labelPhotos: IAttachment[];
}

// Predvyplnenie pri editácii už pridaného riadku (tlačidlo "pen" v tabuľkách v DeviceDialog) —
// na rozdiel od presetTypeCode NEzablokuje polia, len ich predvyplní, nech sa dajú opraviť
export interface IInstalledDeviceEditInitial {
  typeCode: string;
  specificationId: string;
  manufacturerCode: string;
  energeticDeviceId: string;
  modelName: string;
  count: number;
  labelPhotos: IAttachment[];
}

interface IProps {
  open: boolean;
  onClose: () => void;
  // continueAfter: true = "Uložiť a pokračovať" (potvrdí výber a rovno uloží celý dialóg zariadenia)
  onConfirm: (selection: IInstalledDeviceSelection, continueAfter: boolean) => void;
  // Ak je zadané (napr. 'VC' pre Menič napätia), pole "Druh zariadenia" sa predvyplní touto
  // hodnotou a zablokuje na needit — použité pri vstupe z tlačidla "Výber meniča napätia"
  presetTypeCode?: string | null;
  // Editácia existujúceho riadku — predvyplní Druh/Špecifikácia/Výrobca/Model/Počet/Fotky, polia
  // ostávajú editovateľné
  editInitial?: IInstalledDeviceEditInitial | null;
}

function InstalledDeviceSelectDialog({ open, onClose, onConfirm, presetTypeCode = null, editInitial = null }: IProps) {
  const { t } = useTranslation();
  // Druh zariadenia — plnený zo servera (číselník GD.CL.Data.EnergeticDeviceType), viď useServerCall nižšie
  const [typeCode, setTypeCode] = useState<string | null>(null);
  // Špecifikácia — plnená zo servera (číselník GD.CL.Data.EnergeticDeviceSpecification),
  // filtrovaná podľa energetic_device_type_id zvoleného druhu zariadenia
  const [specificationId, setSpecificationId] = useState<string | null>(null);
  const [specificationOptions, setSpecificationOptions] = useState<ICodelistRow[]>([]);
  // Výrobca — plnený zo servera (číselník GD.CL.Data.Manufacturer)
  const [manufacturerCode, setManufacturerCode] = useState<string | null>(null);
  // Model — vyhľadaný zo servera (POST /gd/record/energetic-device), automaticky dotiahnutý,
  // len čo sú vyplnené všetky tri predchádzajúce povinné polia (druh/špecifikácia/výrobca)
  const [selectedModelRow, setSelectedModelRow] = useState<ICodelistRow | null>(null);
  const [modelOptions, setModelOptions] = useState<ICodelistRow[]>([]);
  const [count, setCount] = useState<number | ''>(1);
  // Dva nezávislé upload sloty (podľa dizajnu) — pri uložení sa spoja do jedného zoznamu príloh
  const [labelPhotosA, setLabelPhotosA] = useState<IAttachment[]>([]);
  const [labelPhotosB, setLabelPhotosB] = useState<IAttachment[]>([]);
  // Modál "Pridať zariadenie (do číselníka zariadení)" — pre prípad, že hľadaný model nie je
  // v číselníku; zatiaľ len UI shell, bez napojenia na ukladanie
  const [addNewDeviceOpen, setAddNewDeviceOpen] = useState(false);

  const energeticDeviceTypeCall = useServerCall<unknown, { rows: ICodelistRow[] }>({
    key: 'energeticDeviceTypeCodelist',
    mode: 'control',
    request: { method: 'POST', url: API.energeticDeviceTypeCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      CodelistStore.setCodelist('energetic_device_type', res.data?.rows ?? []);
    }
  });

  // Druh zariadenia sa filtruje podľa device_group_id kola (devide_group_id z /rounds — 'ele' | 'heat')
  useEffect(() => {
    if (DeviceDialogStore.deviceGroupId) {
      energeticDeviceTypeCall.run({ data: getFilters([`device_group_id=${DeviceDialogStore.deviceGroupId}`]) });
    }
  }, [DeviceDialogStore.deviceGroupId]);

  useServerCall<undefined, { rows: ICodelistRow[] }>({
    key: 'manufacturerCodelist',
    mode: 'auto',
    request: { method: 'GET', url: API.manufacturerCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      CodelistStore.setCodelist('manufacturer', res.data?.rows ?? []);
    }
  });

  const typeOptions = CodelistStore.getCodelist<ICodelistRow>('energetic_device_type');
  const selectedType = typeOptions.find((o) => o.code_ext === typeCode) ?? null;

  const specificationCall = useServerCall<unknown, { rows: ICodelistRow[] }>({
    key: 'energeticDeviceSpecificationByType',
    mode: 'control',
    request: { method: 'POST', url: API.energeticDeviceSpecificationCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      setSpecificationOptions(res.data?.rows ?? []);
    }
  });

  // Špecifikácie závisia od zvoleného druhu zariadenia — dotiahnuť nanovo pri každej zmene typu
  useEffect(() => {
    if (selectedType?._id) {
      specificationCall.run({ data: getFilters([`energetic_device_type_id=${selectedType._id}`]) });
    } else {
      setSpecificationOptions([]);
    }
  }, [selectedType?._id]);

  useEffect(() => {
    if (open) {
      if (editInitial) {
        setTypeCode(editInitial.typeCode);
        setSpecificationId(editInitial.specificationId);
        setManufacturerCode(editInitial.manufacturerCode);
        setCount(editInitial.count);
        setLabelPhotosA(editInitial.labelPhotos);
        setLabelPhotosB([]);
      } else {
        setTypeCode(presetTypeCode);
        setSpecificationId(null);
        setManufacturerCode(null);
        setCount(1);
        setLabelPhotosA([]);
        setLabelPhotosB([]);
      }
      // Model sa predvyplní nižšie, po dotiahnutí modelOptions (viď efekt pod modelCall)
      setSelectedModelRow(null);
    }
  }, [open, presetTypeCode, editInitial]);

  const selectedSpecification = specificationOptions.find((o) => o._id === specificationId) ?? null;
  const manufacturerOptions = CodelistStore.getCodelist<ICodelistRow>('manufacturer');
  const selectedManufacturer = manufacturerOptions.find((o) => o.code_ext === manufacturerCode) ?? null;

  const clearModel = () => {
    setSelectedModelRow(null);
  };

  const modelCall = useServerCall<unknown, { rows: ICodelistRow[] }>({
    key: 'energeticDeviceByFilter',
    mode: 'control',
    request: { method: 'POST', url: API.energeticDeviceSearch() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      setModelOptions(res.data?.rows ?? []);
    }
  });

  // Model závisí od všetkých troch predchádzajúcich polí — dotiahnuť dáta len keď sú vyplnené všetky tri,
  // a znova zakaždým, keď sa ktorékoľvek z nich zmení (výsledky tak ostanú aktuálne)
  useEffect(() => {
    if (selectedType?._id && selectedSpecification?._id && selectedManufacturer?._id) {
      modelCall.run({
        data: getFilters([
          `energetic_device_type_id=${selectedType._id}`,
          `energetic_device_specification_id=${selectedSpecification._id}`,
          `manufacturer_id=${selectedManufacturer._id}`
        ])
      });
    } else {
      setModelOptions([]);
      setSelectedModelRow(null);
    }
  }, [selectedType?._id, selectedSpecification?._id, selectedManufacturer?._id]);

  // Pri editácii existujúceho riadku predvyplniť aj pole "Model zariadenia", len čo sa dotiahnu
  // modelOptions zodpovedajúce predvyplnenému Druhu/Špecifikácii/Výrobcovi
  useEffect(() => {
    if (!editInitial || selectedModelRow) return;
    const match = modelOptions.find((o) => (o.energetic_device_id as string | undefined) === editInitial.energeticDeviceId);
    if (match) setSelectedModelRow(match);
  }, [editInitial, modelOptions, selectedModelRow]);

  const isComplete =
    !!selectedType &&
    !!selectedSpecification &&
    !!selectedManufacturer &&
    !!selectedModelRow &&
    count !== '' &&
    Number(count) >= 1;

  const buildSelection = (): IInstalledDeviceSelection | null => {
    if (!selectedType || !selectedSpecification || !selectedManufacturer || !selectedModelRow) return null;
    const modelName = (selectedModelRow.model_name as string | undefined) ?? selectedModelRow.name_ext ?? '';
    const modelId =
      (selectedModelRow.energetic_device_id as string | undefined) ??
      (selectedModelRow.energetic_device_id as string) ??
      '';

    const device: IDeviceRow = {
      _id: modelId,
      code_ext: (selectedModelRow.code_ext as string) ?? selectedType.code_ext,
      name_ext: modelName,
      device_type_name: selectedType.name_ext,
      manufacturer: selectedManufacturer.name_ext,
      model_type: modelName
    };
    return {
      device,
      deviceTypeCode: selectedType.code_ext ?? '',
      specificationName: selectedSpecification.name_ext ?? '',
      specificationId: selectedSpecification._id,
      manufacturerCode: selectedManufacturer.code_ext ?? selectedManufacturer._id,
      count: Number(count),
      labelPhotos: [...labelPhotosA, ...labelPhotosB]
    };
  };

  const handleConfirm = (continueAfter: boolean) => {
    const selection = buildSelection();
    if (!selection) return;
    onConfirm(selection, continueAfter);
  };

  return (
    <XsBaseDialog
      open={open}
      onClose={onClose}
      title={t('device_dialog.select_installed_title')}
      maxWidth='sm'
      fullWidth
    >
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <XsSearchSelect<ICodelistRow>
              name='installedDeviceType'
              label={t('device_dialog.installed_type')}
              required
              disabled={!!presetTypeCode}
              value={selectedType}
              options={typeOptions}
              getOptionLabel={(o) => o.name_ext ?? ''}
              getOptionValue={(o) => o.code_ext ?? o._id}
              onChange={(v: ICodelistRow | null) => {
                setTypeCode(v?.code_ext ?? null);
                setSpecificationId(null);
                setManufacturerCode(null);
                clearModel();
              }}
              onBlur={() => {}}
            />
          </Grid>

          <Grid item xs={12}>
            <XsSearchSelect<ICodelistRow>
              name='installedDeviceSpecification'
              label={t('device_dialog.installed_specification')}
              required
              disabled={!typeCode}
              value={selectedSpecification}
              options={specificationOptions}
              getOptionLabel={(o) => o.name_ext ?? ''}
              getOptionValue={(o) => o._id}
              onChange={(v: ICodelistRow | null) => {
                setSpecificationId(v?._id ?? null);
                setManufacturerCode(null);
                clearModel();
              }}
              onBlur={() => {}}
            />
          </Grid>

          <Grid item xs={12}>
            <XsSearchSelect<ICodelistRow>
              name='installedDeviceManufacturer'
              label={t('device_dialog.installed_manufacturer')}
              required
              value={selectedManufacturer}
              options={manufacturerOptions}
              getOptionLabel={(o) => o.name_ext ?? ''}
              getOptionValue={(o) => o.code_ext ?? o._id}
              onChange={(v: ICodelistRow | null) => {
                setManufacturerCode(v?.code_ext ?? null);
                clearModel();
              }}
              onBlur={() => {}}
            />
          </Grid>

          <Grid item xs={12}>
            {/* <Tooltip title={String(t('device_dialog.installed_model_tooltip'))} placement='right'> */}
            <XsSearchSelect<ICodelistRow>
              name='installedDeviceModel'
              label={t('device_dialog.installed_model')}
              required
              disabled={!selectedManufacturer}
              value={selectedModelRow}
              options={modelOptions}
              getOptionLabel={(o) => (o.model_name as string | undefined) ?? o.name_ext ?? ''}
              getOptionValue={(o) => o._id}
              onChange={(v: ICodelistRow | null) => setSelectedModelRow(v)}
              onBlur={() => {}}
            />
            {/* </Tooltip> */}
          </Grid>

          <Grid item xs={12}>
            <XsNumberField
              name='installedDeviceCount'
              label={t('device_dialog.count')}
              required
              value={count}
              onChange={(e: any) => setCount(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
              {t('device_dialog.att_label_photo')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <XsDropzone
                  name='dropzone_installedDeviceLabelPhotoA'
                  maxFiles={10}
                  accept={['.pdf', '.jpg', '.jpeg', '.png']}
                  value={labelPhotosA}
                  onChange={(files) => setLabelPhotosA(files as IAttachment[])}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <XsDropzone
                  name='dropzone_installedDeviceLabelPhotoB'
                  maxFiles={10}
                  accept={['.pdf', '.jpg', '.jpeg', '.png']}
                  value={labelPhotosB}
                  onChange={(files) => setLabelPhotosB(files as IAttachment[])}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <XsButton fullWidth={false} label='global.back' variant='outlined' onClick={onClose} />
          </Grid>
          <Grid item>
            <Grid container spacing={1}>
              <Grid item>
                <XsButton
                  fullWidth={false}
                  label='device_dialog.select_add_button'
                  variant='outlined'
                  color='success'
                  disabled={!selectedType || !selectedSpecification || !selectedManufacturer}
                  onClick={() => setAddNewDeviceOpen(true)}
                />
              </Grid>
              <Grid item>
                <XsButton
                  fullWidth={false}
                  label='device_dialog.select_save_continue'
                  variant='contained'
                  color='success'
                  disabled={!isComplete}
                  onClick={() => handleConfirm(true)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      <AddNewDeviceDialog
        open={addNewDeviceOpen}
        onClose={() => setAddNewDeviceOpen(false)}
        onConfirm={() => {
          setAddNewDeviceOpen(false);
          // Nové zariadenie bolo pridané do číselníka — dotiahnuť zoznam modelov nanovo,
          // nech je v poli "Model zariadenia" hneď k dispozícii
          if (selectedType?._id && selectedSpecification?._id && selectedManufacturer?._id) {
            modelCall.run({
              data: getFilters([
                `energetic_device_type_id=${selectedType._id}`,
                `energetic_device_specification_id=${selectedSpecification._id}`,
                `manufacturer_id=${selectedManufacturer._id}`
              ])
            });
          }
        }}
        presetTypeCode={selectedType?.code_ext ?? null}
        presetSpecificationId={selectedSpecification?._id ?? null}
        presetManufacturerCode={selectedManufacturer?.code_ext ?? null}
      />
    </XsBaseDialog>
  );
}

export default observer(InstalledDeviceSelectDialog);

